// useCopilotPlans: the "which plan is live, and what happens when you build
// it" logic, shared by every surface that renders the conversation.
//
// Extracted rather than copied: the panel and the Home tab are two views of ONE
// chat, so duplicating "find the newest un-built plan" and "commit it" into
// both is how they'd start disagreeing. One copy, two callers.

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edgeError";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminChat } from "@/contexts/AdminChatContext";
import { findPendingPlanId } from "@/lib/workflows/pendingPlan";

/** Commits the plan staged on one assistant turn. Returns the new draft's id. */
export type CommitPlan = (messageId: string) => Promise<string | null>;

export interface UseCopilotPlans {
  /** The only plan with live controls. The newest one nobody has built. */
  pendingPlanId: string | null;
  /** The turn currently being committed, if any. */
  committingId: string | null;
  buildPlan: (messageId: string) => Promise<void>;
}

export function useCopilotPlans(input: {
  /** Called with the new draft's id once a plan is built. */
  onWorkflowCreated: (workflowId: string) => void;
  /** Overrides the live commit. Dev/preview use only. */
  commitPlan?: CommitPlan;
}): UseCopilotPlans {
  const { messages, markPlanBuilt } = useAdminChat();
  const { businessId } = useAuth();
  const queryClient = useQueryClient();
  const [committingId, setCommittingId] = useState<string | null>(null);

  const pendingPlanId = findPendingPlanId(messages);

  const commit = input.commitPlan;
  const onWorkflowCreated = input.onWorkflowCreated;

  const buildPlan = useCallback(
    async (messageId: string) => {
      setCommittingId(messageId);
      try {
        let workflowId: string | null;
        if (commit) {
          workflowId = await commit(messageId);
        } else {
          const { data, error } = await supabase.functions.invoke("admin-assistant", {
            body: { action: "build_workflow", message_id: messageId },
          });
          if (error) throw new Error(await readEdgeError(error));
          // The cap can fill between staging a plan and building it, so a
          // refusal carries copy written for the owner, not an error code.
          if (!data?.ok) throw new Error(data?.reply ?? "Couldn't build that workflow just yet.");
          workflowId = (data.workflow_id as string) ?? null;
          queryClient.invalidateQueries({ queryKey: ["workflow-definitions", businessId] });
        }
        if (workflowId) {
          markPlanBuilt(messageId, workflowId);
          onWorkflowCreated(workflowId);
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Couldn't build that workflow just yet.");
      } finally {
        setCommittingId(null);
      }
    },
    [businessId, commit, markPlanBuilt, onWorkflowCreated, queryClient],
  );

  return { pendingPlanId, committingId, buildPlan };
}
